import { KeyboardArrowLeft, Send, Image, Close } from "@mui/icons-material";
import { Box, Divider, Grid, InputBase, Stack, IconButton, Typography } from "@mui/material";
import { shiftNotesApi } from "api";
import { ShiftNoteEntity } from "api/generated";
import AppModal from "components/AppModal";
import FlexBetween from "components/flexbox/FlexBetween";
import Scrollbar from "components/ScrollBar";
import { StyledIconButton } from "components/TenantDisplay";
import { H2, H4, Small } from "components/Typography";
import { format } from "date-fns";
import ShiftNotesBubble from "page-sections/notes/ShiftNotesBubble";
import React, { ChangeEvent, useEffect, FC, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

interface ModalProps {
  notes: ShiftNoteEntity[];
  open: boolean;
  onClose: () => void;
  fetchData: () => void;
  date?: Date;
  // Passed as query params so DEPCHIEF users pass the backend permission guard.
  departmentId?: number;
  seasonId?: number;
}

const ShiftNotesModal: FC<ModalProps> = ({ notes, open, onClose, fetchData, date, departmentId, seasonId }) => {
  const [note, setNote] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const notesEnd = React.useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [noteActionId, setNoteActionId] = useState(-1);
  const [disableHover, setDisableHover] = useState(false);
  const { t } = useTranslation();

  const authParams = { params: { departmentId, seasonId } };

  const handleNoteChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
  };

  const handleImageSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const createNote = () => {
    if (date) {
      if (selectedImage) {
        // Create note with image using the generated API
        const formData = new FormData();
        formData.append('text', note || ' '); // Allow empty text
        formData.append('date', new Date(date.setUTCHours(0, 0, 0, 0)).toISOString());
        formData.append('image', selectedImage);

        shiftNotesApi.createWithImage({
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          params: { departmentId, seasonId }
        }).then(() => {
          fetchData();
          setDisableHover(true);
          setNote('');
          removeImage();
        }).catch((error) => {
          console.error('Error creating shift note with image:', error);
        });
      } else {
        // Create note without image
        shiftNotesApi.create(
          {
            date: new Date(date.setUTCHours(0, 0, 0, 0)).toISOString(),
            text: note || ' ' // Allow empty text
          },
          authParams
        ).then(() => {
          fetchData();
          setDisableHover(true);
        })
        setNote('');
      }
    }
  };

  const editNote = (note: ShiftNoteEntity) => {
    setNoteActionId(note.id);
    setNote(note.text);

    const inputField = document.getElementById('shiftNoteInput');
    if (inputField) {
      inputField.focus();
    }
  };

  const updateNote = () => {
    if (noteActionId > 0) {
      shiftNotesApi.update(
        String(noteActionId),
        {
          text: note || ' ' // Allow empty text
        },
        authParams
      ).then(() => {
        fetchData();
        setNote('');
        setNoteActionId(-1);
      })
    }
  }

  const resetEditModeNote = () => {
    setNote('');
    setNoteActionId(-1);
  };

  useEffect(() => {
    notesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [notes]);

  return (
    <AppModal
      open={open}
      handleClose={() => {
        onClose();
        setNote('');
        removeImage();
      }}
      sx={{ width: 500 }}
    >
      <H2>{t('shiftNotes.title')}</H2>
      <H4>{date && format(date, 'dd/MM/yyyy')}</H4>
      <Grid container justifyContent="center">
        <Grid item xl={12} md={12} xs={12}>
          <Box
            position="relative"
            onMouseMove={() => {
              if (disableHover) {
                setDisableHover(false);
              }
            }}
          >
            {noteActionId > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(1px)',
                  zIndex: 1,
                }}
              />
            )}

            <Scrollbar style={{ height: 400 }}>
              <Stack spacing={4} px={3} py={2}>
                {notes.sort(function (a, b) { return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() }).map((note) => (
                  <div ref={notesEnd} key={note.id}>
                    <ShiftNotesBubble
                      noteInstance={note}
                      fetchData={fetchData}
                      editNoteCallback={editNote}
                      disableHover={disableHover}
                      departmentId={departmentId}
                      seasonId={seasonId}
                    />
                  </div>
                ))}
              </Stack>
            </Scrollbar>

          </Box>

          {noteActionId > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'rgba(92, 84, 112, 0.3)' }}>
              <KeyboardArrowLeft fontSize="small"
                onClick={resetEditModeNote}
              />
              <Small fontWeight={600} fontSize={10}>{t('notes.edit')}</Small>
            </div>
          )}

          <Divider />

          {/* Image Preview */}
          {imagePreview && (
            <Box sx={{ p: 2, position: 'relative' }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                {t('notes.imagePreview')}:
              </Typography>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '150px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}
                />
                <IconButton
                  size="small"
                  onClick={removeImage}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: 'error.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'error.dark',
                    }
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}

          <FlexBetween padding={2} gap={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <InputBase
                id="shiftNoteInput"
                fullWidth
                multiline
                placeholder={t('notes.inputPlaceholder')}
                sx={{ fontSize: 12, fontWeight: 500 }}
                value={note}
                onChange={handleNoteChange}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    noteActionId < 0 ? createNote() : updateNote();
                  }
                }}
              />

              {/* Image Upload Button */}
              {noteActionId < 0 && (
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />
              )}

              {noteActionId < 0 && (
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    color: selectedImage ? 'primary.main' : 'text.secondary',
                    '&:hover': {
                      color: 'primary.main',
                    }
                  }}
                >
                  <Image />
                </IconButton>
              )}
            </Box>

            <StyledIconButton
              onClick={() => { noteActionId < 0 ? createNote() : updateNote() }}
              disabled={noteActionId >= 0}
            >
              <Send sx={{ color: noteActionId >= 0 ? 'gray' : 'primary.main' }} />
            </StyledIconButton>
          </FlexBetween>
        </Grid>
      </Grid>
    </AppModal>
  )
}

export default ShiftNotesModal;
